import { TruncatedText } from '@/shared/ui/TruncatedText';
import { ArtifactVersionBadge } from '../../ArtifactVersionBadge';
import { getArtifactDisplayName } from '../../../lib/artifact-display-name';
import type { WorkspaceArtifactRow } from '../ExistingArtifactsPicker.types';

interface Props {
  row: WorkspaceArtifactRow;
}

/**
 * Two-line cell that disambiguates identical agent runs across different
 * chats. Lines swap roles based on creatorType:
 * - Agent artifacts have a generic `name` ("Image Generation Widget") so the
 *   chat `contextName` ("Generate an Image of a Cactus") is the
 *   user-meaningful primary line; the secondary line shows the widget name
 *   with the version rendered as a separate badge (matches the Library
 *   card's `v{n}` outline badge).
 * - File artifacts (creatorType=user) have a specific `name` (the uploaded
 *   filename) so that becomes the primary line and the chat `contextName`
 *   becomes the secondary line.
 * Each line truncates independently with its own Tooltip on overflow.
 */
export function ArtifactNameCell(props: Props) {
  const { row } = props;
  const isFile = row.creatorType === 'user';
  const name = isFile ? row.name : getArtifactDisplayName(row.name);
  const primary = isFile ? name : (row.contextName ?? name);
  const secondary = isFile ? row.contextName : row.contextName != null ? name : null;
  return (
    <div className="flex min-w-0 flex-col">
      <TruncatedText value={primary} className="font-medium text-foreground" />
      {secondary != null && (
        <div className="flex min-w-0 items-center gap-1.5">
          <TruncatedText value={secondary} className="text-xs text-muted-foreground" />
          {!isFile && row.version != null && <ArtifactVersionBadge version={row.version} />}
        </div>
      )}
    </div>
  );
}
